import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { queryKeys } from "@/lib/query-keys";
import { stockService } from "@/services/stock";

import type { StockFilterTab } from "./stock-filter-bar";
import type { StockItem } from "./types";
import type { RawMaterialKind, StockItemType } from "@artisancode/api-types";

export function useStocks() {
  const qc = useQueryClient();
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<StockFilterTab>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const isKind =
    typeFilter === "purchased" ||
    typeFilter === "ingredient" ||
    typeFilter === "direct";
  const apiType: StockItemType | undefined =
    typeFilter === "product" ? "product" : isKind ? "raw_material" : undefined;
  const apiKind = isKind ? (typeFilter as RawMaterialKind) : undefined;

  const {
    data: stockListRes,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: queryKeys.stocks.list({
      q: query,
      type: typeFilter,
      status: statusFilter,
    }),
    queryFn: () =>
      stockService.list({
        q: query || undefined,
        type: apiType,
        kind: apiKind,
        status:
          statusFilter !== "all"
            ? (statusFilter as "in_stock" | "low_stock" | "out_of_stock")
            : undefined,
        page: 1,
        per_page: 500,
      }),
  });

  const { data: metricsRes } = useQuery({
    queryKey: queryKeys.stocks.metrics(),
    queryFn: () => stockService.metrics(),
  });

  const stockItems: StockItem[] = useMemo(() => {
    if (!stockListRes?.items) return [];
    return stockListRes.items.map((item) => ({
      id: item.id,
      itemId: item.itemId,
      name: item.name,
      sku: item.sku,
      type: item.itemType,
      kind: item.kind,
      categoryName: item.categoryName,
      uomName: item.uomName,
      onHandQty: item.onHandQty,
      repackingQty: item.repackingQty,
      riderQty: item.riderQty,
      currentStock: item.totalStock,
      minStock: item.minStock,
      costPerUnit: item.costPerUnit,
      totalValue: item.totalValue,
      status: item.status,
    }));
  }, [stockListRes]);

  const filteredItems = stockItems;

  const totalItems = metricsRes?.totalItems ?? stockItems.length;
  const inStockCount =
    metricsRes?.inStockCount ??
    stockItems.filter((i) => i.status === "in_stock").length;
  const lowStockCount =
    metricsRes?.lowStockCount ??
    stockItems.filter((i) => i.status === "low_stock").length;
  const outOfStockCount =
    metricsRes?.outOfStockCount ??
    stockItems.filter((i) => i.status === "out_of_stock").length;
  const totalAssetValue =
    metricsRes?.totalAssetValue ??
    stockItems.reduce((acc, curr) => acc + (curr.totalValue || 0), 0);

  function handleResetDemo() {
    setQuery("");
    setTypeFilter("all");
    setStatusFilter("all");
    toast.info("Tampilan filter telah direset.");
  }

  function handleSelectStatusFilter(status: string) {
    setStatusFilter((prev) => (prev === status ? "all" : status));
  }

  function handleUpdateItemMinStock() {
    qc.invalidateQueries({ queryKey: queryKeys.stocks.all });
  }

  return {
    stockItems,
    filteredItems,
    query,
    setQuery,
    typeFilter,
    setTypeFilter: (val: StockFilterTab) => setTypeFilter(val),
    statusFilter,
    setStatusFilter,
    handleSelectStatusFilter,
    handleResetDemo,
    handleUpdateItemMinStock,
    totalItems,
    inStockCount,
    lowStockCount,
    outOfStockCount,
    totalAssetValue,
    isLoading,
    refetch,
  };
}
